// A setTimeout that only counts time while the tab is visible (#139).
//
// A hidden tab does not stop timers, it only throttles them — but the ad does
// stop: AdSense pauses the break and the player is not there to watch it. A
// deadline meant for "the ad network went silent" was firing on "the player
// went to lunch", and the reward was lost before they came back.
//
// `restart()` re-arms the clock with a fresh budget, which is how a caller
// turns a hard deadline into a silence detector.
//
// Copied between the sdk and plugin bundles, like communication.js: the two
// ship separately and share no package.
export function startVisibleTimer(durationMs, onElapsed) {
  const doc = typeof document !== 'undefined' ? document : null;
  let remaining = durationMs;
  let runningSince = null;
  let timer = null;
  let finished = false;

  const pause = () => {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
    remaining -= Date.now() - runningSince;
    runningSince = null;
  };

  const resume = () => {
    if (finished || timer !== null) return;
    runningSince = Date.now();
    timer = setTimeout(fire, Math.max(remaining, 0));
  };

  const onVisibilityChange = () => {
    if (doc.hidden) pause();
    else resume();
  };

  const detach = () => {
    if (doc) doc.removeEventListener('visibilitychange', onVisibilityChange);
  };

  function fire() {
    timer = null;
    runningSince = null;
    finished = true;
    detach();
    onElapsed();
  }

  if (doc) doc.addEventListener('visibilitychange', onVisibilityChange);
  // Without a document there is nothing to hide behind, so the timer just runs.
  if (!doc || !doc.hidden) resume();

  return {
    cancel() {
      finished = true;
      pause();
      detach();
    },
    restart(nextMs) {
      if (finished) return;
      pause();
      remaining = nextMs === undefined ? durationMs : nextMs;
      if (!doc || !doc.hidden) resume();
    },
  };
}
